import type {
  FactoriesFactory,
  FactoriesLineRef,
  FactoriesWorkOrderExecution,
  FactoriesWorkOrderLineDispatch,
  FactoriesWorkOrderSummary,
} from "@/api-client";
import { selectWorkOrderCardPullRequest, workOrderCardPullRequestShowsMergeable } from "./workOrderCardPullRequest";
import { workOrderListSource } from "./workOrderCardSource";
import { pullRequestState } from "./workOrderPullRequest";
import { workOrderMatchesUser } from "./workOrderListPagination";
import { isActiveWorkOrderExecution } from "./workOrderExecutions";
import { formatDurationSeconds, formatUsdCents, formatWorkOrderUsage, parseWorkOrderMetric } from "./workOrderUsage";
import {
  WORK_ORDER_BOARD_LANES,
  WORK_ORDER_DISPLAY_STATUSES,
  getWorkOrderDisplayKey,
  getWorkOrderDisplayStatus,
  isUnassignedWorkOrder,
  type WorkOrderBoardLaneId,
  type WorkOrderDisplayStatus,
} from "./workOrderProgress";

/**
 * Presentation model built on top of a listed work order. It centralizes
 * every derived value the Tasks layouts need — identifiers, status,
 * latest line and step, usage, assignee summary, and search text — so
 * board/list/table stay display-only and can share the same reducers.
 *
 * The backend still owns the raw payload; this module is just a lens over
 * it. `workspace-identity-backend` filled in `key`/`number`; here we add
 * everything else the reference application shows.
 */
export interface WorkOrderListEntry {
  order: FactoriesWorkOrderSummary;
  id: string;
  displayKey: string;
  /** Numeric part of `displayKey`, used to order by ID. */
  displayNumber: number;
  title: string;
  displayStatus: WorkOrderDisplayStatus;
  latestExecution: FactoriesWorkOrderExecution | null;
  latestLineName: string | null;
  latestStepName: string | null;
  /** Caption every layout shows under the title, empty when nothing ran yet. */
  lineStepLabel: string;
  lineIds: string[];
  lineNames: string[];
  distinctLineCount: number;
  totalTokens: number;
  totalCostCents: number;
  durationSeconds: number;
  usageLabel: string | null;
  usageTooltip: string | null;
  updatedAtMs: number;
  createdAtMs: number;
  assigneeIds: string[];
  assigneeNames: string[];
  isUnassigned: boolean;
  sourceId: string;
  sourceLabel: string;
  searchHaystack: string;
  isDispatchable: boolean;
}

export function buildWorkOrderListEntry(
  order: FactoriesWorkOrderSummary,
  factory: FactoriesFactory | null | undefined,
): WorkOrderListEntry {
  const dispatches = order.lineDispatches ?? [];
  const pairs = flattenDispatchExecutions(dispatches);
  const latestPair = findLatestExecution(pairs);
  const lines = collectLines(dispatches);
  const { totalTokens, totalCostCents, durationSeconds } = sumUsage(
    order,
    pairs.map((pair) => pair.execution),
  );
  const { assigneeIds, assigneeNames } = collectAssignees(order);
  const source = workOrderListSource(order);

  const createdAtMs = parseTimestamp(order.createdAt);
  const displayKey = getWorkOrderDisplayKey(order, factory?.key ?? null);
  const title = trimOrNull(order.title) ?? "Untitled task";
  const latestLineName = trimOrNull(latestPair?.line?.name);
  const latestStepName = trimOrNull(latestPair?.execution.step);

  return {
    order,
    id: order.id ?? "",
    displayKey,
    displayNumber: parseIdentifierNumber(displayKey),
    title,
    displayStatus: getWorkOrderDisplayStatus(order),
    latestExecution: latestPair?.execution ?? null,
    latestLineName,
    latestStepName,
    lineStepLabel: buildLineStepLabel(latestLineName, latestStepName, lines.ids.length),
    lineIds: lines.ids,
    lineNames: lines.names,
    distinctLineCount: lines.ids.length,
    totalTokens,
    totalCostCents,
    durationSeconds,
    usageLabel: formatWorkOrderUsage(totalTokens, totalCostCents, durationSeconds),
    usageTooltip: formatUsageTooltip(totalTokens, totalCostCents, durationSeconds),
    updatedAtMs: parseTimestamp(order.updatedAt) || createdAtMs,
    createdAtMs,
    assigneeIds,
    assigneeNames,
    isUnassigned: isUnassignedWorkOrder(order),
    sourceId: source.id,
    sourceLabel: source.label,
    searchHaystack: buildSearchHaystack([
      displayKey,
      title,
      order.description,
      lines.names.join(" "),
      latestStepName,
      assigneeNames.join(" "),
      source.label,
    ]),
    isDispatchable: isDispatchableState(order.state),
  };
}

function parseTimestamp(value: string | undefined): number {
  return Date.parse(value ?? "") || 0;
}

function trimOrNull(value: string | undefined): string | null {
  const trimmed = value?.trim();
  return trimmed ? trimmed : null;
}

function isDispatchableState(state: FactoriesWorkOrderSummary["state"]): boolean {
  return state === "STATE_DRAFT" || state === "STATE_OPEN";
}

function buildSearchHaystack(parts: Array<string | null | undefined>): string {
  return parts.filter(Boolean).join(" ").toLowerCase();
}

/**
 * "line · step" for the newest execution, plus a count of the other lines
 * the order touched. Built here so Board, List, and Table cannot drift
 * apart on the wording.
 */
function buildLineStepLabel(lineName: string | null, stepName: string | null, distinctLineCount: number): string {
  const label = [lineName, stepName].filter(Boolean).join(" · ");
  if (!label) {
    return "";
  }
  const otherLines = distinctLineCount - 1;
  if (otherLines < 1) {
    return label;
  }
  return `${label} · +${otherLines} more line${otherLines === 1 ? "" : "s"}`;
}

/** One step execution paired with the line ref of its parent dispatch —
 * `FactoriesWorkOrderExecution` no longer carries its own `line`, that
 * moved to the parent `FactoriesWorkOrderLineDispatch`. */
interface ExecutionWithLine {
  execution: FactoriesWorkOrderExecution;
  line?: FactoriesLineRef;
}

function flattenDispatchExecutions(dispatches: FactoriesWorkOrderLineDispatch[]): ExecutionWithLine[] {
  const pairs: ExecutionWithLine[] = [];
  for (const dispatch of dispatches) {
    for (const execution of dispatch.stepExecutions ?? []) {
      pairs.push({ execution, line: dispatch.line });
    }
  }
  return pairs;
}

/** Distinct lines the order has run on (one or more dispatches), in first-seen order. */
function collectLines(dispatches: FactoriesWorkOrderLineDispatch[]): { ids: string[]; names: string[] } {
  const ids = new Set<string>();
  const names = new Set<string>();
  for (const dispatch of dispatches) {
    const id = dispatch.line?.id?.trim();
    if (id) {
      ids.add(id);
    }
    const name = dispatch.line?.name?.trim();
    if (name) {
      names.add(name);
    }
  }
  return { ids: [...ids], names: [...names] };
}

/**
 * Order-level totals win when the backend has them; otherwise we add up
 * the executions so older payloads still show usage.
 */
function sumUsage(
  order: FactoriesWorkOrderSummary,
  executions: FactoriesWorkOrderExecution[],
): { totalTokens: number; totalCostCents: number; durationSeconds: number } {
  const totalTokens = parseWorkOrderMetric(order.totalTokens);
  const totalCostCents = parseWorkOrderMetric(order.totalCostCents);
  const durationSeconds = parseWorkOrderMetric(order.totalDurationSeconds);
  if (totalTokens > 0 || totalCostCents > 0 || durationSeconds > 0) {
    return { totalTokens, totalCostCents, durationSeconds };
  }
  return executions.reduce(
    (sum, execution) => ({
      totalTokens: sum.totalTokens + parseWorkOrderMetric(execution.totalTokens),
      totalCostCents: sum.totalCostCents + parseWorkOrderMetric(execution.costCents),
      durationSeconds: sum.durationSeconds + parseWorkOrderMetric(execution.durationSeconds),
    }),
    { totalTokens: 0, totalCostCents: 0, durationSeconds: 0 },
  );
}

function collectAssignees(order: FactoriesWorkOrderSummary): { assigneeIds: string[]; assigneeNames: string[] } {
  const owner = order.assignees?.[0];
  if (!owner?.id) {
    return { assigneeIds: [], assigneeNames: [] };
  }
  const name = (owner.name ?? "").trim();
  return {
    assigneeIds: [owner.id],
    assigneeNames: name ? [name] : [],
  };
}

export function buildWorkOrderListEntries(
  orders: FactoriesWorkOrderSummary[],
  factory: FactoriesFactory | null | undefined,
): WorkOrderListEntry[] {
  return orders
    .filter((order) => order.result !== "RESULT_ARCHIVED")
    .map((order) => buildWorkOrderListEntry(order, factory));
}

/**
 * Latest execution across every line dispatch, ordered by `updatedAt` then
 * `createdAt`. An active execution wins ties so an in-flight step surfaces
 * over a stale finished one that happens to share a timestamp. "Active"
 * must match the predicate behind the Running display status, or a tie can
 * show a line and step that disagree with the status pill.
 */
function findLatestExecution(pairs: ExecutionWithLine[]): ExecutionWithLine | null {
  if (pairs.length === 0) {
    return null;
  }
  let winner: ExecutionWithLine | null = null;
  let winnerAt = -Infinity;
  for (const pair of pairs) {
    const at = Date.parse(pair.execution.updatedAt ?? pair.execution.createdAt ?? "") || 0;
    if (at > winnerAt || (at === winnerAt && isActiveWorkOrderExecution(pair.execution))) {
      winner = pair;
      winnerAt = at;
    }
  }
  return winner;
}

function formatUsageTooltip(totalTokens: number, totalCostCents: number, durationSeconds: number): string | null {
  const parts: string[] = [];
  if (totalCostCents > 0) {
    parts.push(formatUsdCents(totalCostCents));
  }
  if (totalTokens > 0) {
    parts.push(`${totalTokens.toLocaleString()} tokens`);
  }
  if (durationSeconds > 0) {
    parts.push(formatDurationSeconds(durationSeconds));
  }
  return parts.length > 0 ? parts.join(" · ") : null;
}

/**
 * Scope pills next to the page title. `active` keeps drafts, waiting
 * work, and failed runs. `my` keeps work assigned to the viewer.
 * `unassigned` keeps tasks with no owner, including closed tasks.
 */
export type WorkOrderScope = "all" | "active" | "my" | "unassigned";

export const WORK_ORDER_SCOPES: Array<{ id: WorkOrderScope; label: string; tooltip: string }> = [
  { id: "all", label: "All", tooltip: "Every task in this workspace." },
  { id: "active", label: "Active", tooltip: "Draft, waiting, and failed tasks." },
  {
    id: "my",
    label: "My",
    tooltip: "Tasks you created or started. SuperPlane assigns those to you.",
  },
  { id: "unassigned", label: "Unassigned", tooltip: "Tasks with no owner." },
];

/** Statuses that the Active scope keeps. Running is in flight. */
const ACTIVE_SCOPE_STATUSES: WorkOrderDisplayStatus[] = ["draft", "waiting", "failed"];

/** Ordering options in the Display menu. `updated` is the default. */
export type WorkOrderOrdering = "updated" | "status" | "spend" | "key";

export const WORK_ORDER_ORDERINGS: Array<{ id: WorkOrderOrdering; label: string }> = [
  { id: "updated", label: "Updated" },
  { id: "status", label: "Status" },
  { id: "spend", label: "Spend" },
  { id: "key", label: "ID" },
];

/** Layout for the main body. Persisted across sessions. */
export type WorkOrderLayoutId = "board" | "list" | "table";

export const WORK_ORDER_LAYOUTS: Array<{ id: WorkOrderLayoutId; label: string }> = [
  { id: "board", label: "Board" },
  { id: "list", label: "List" },
  { id: "table", label: "Table" },
];

/** Sentinel assignee filter value that matches tasks with no assignee. */
export const UNASSIGNED_FILTER_VALUE = "unassigned";

export { MANUAL_FILTER_VALUE } from "./workOrderCardSource";

/** Card labels the Filter menu can keep: Review (open PR) and Mergeable. */
export const WORK_ORDER_FILTER_LABELS = ["review", "mergeable"] as const;

export type WorkOrderFilterLabel = (typeof WORK_ORDER_FILTER_LABELS)[number];

export const WORK_ORDER_FILTER_LABEL_META: Record<WorkOrderFilterLabel, { label: string }> = {
  review: { label: "Review" },
  mergeable: { label: "Mergeable" },
};

/**
 * Labels the Filter menu can offer. Mergeable stays hidden until the
 * pull-request merge feature is on, matching the card pill.
 */
export function visibleWorkOrderFilterLabels(showPullRequestMerge = false): WorkOrderFilterLabel[] {
  return WORK_ORDER_FILTER_LABELS.filter((label) => label !== "mergeable" || showPullRequestMerge);
}

/** Drops Mergeable when that pill is not shown, so stored filters cannot hide unmatched tasks. */
export function visibleWorkOrderFilters(filters: WorkOrderFilters, showPullRequestMerge = false): WorkOrderFilters {
  const allowed = new Set(visibleWorkOrderFilterLabels(showPullRequestMerge));
  return {
    ...filters,
    labels: filters.labels.filter((label) => allowed.has(label)),
  };
}

/**
 * Filters chosen in the Filter menu. Each dimension narrows independently
 * (AND across dimensions, OR within one), and an empty array means the
 * dimension is not filtering at all.
 */
export interface WorkOrderFilters {
  statuses: WorkOrderDisplayStatus[];
  labels: WorkOrderFilterLabel[];
  lineIds: string[];
  sourceIds: string[];
  assigneeIds: string[];
}

export const EMPTY_WORK_ORDER_FILTERS: WorkOrderFilters = {
  statuses: [],
  labels: [],
  lineIds: [],
  sourceIds: [],
  assigneeIds: [],
};

export function countWorkOrderFilters(filters: WorkOrderFilters): number {
  return (
    filters.statuses.length +
    filters.labels.length +
    filters.lineIds.length +
    filters.sourceIds.length +
    filters.assigneeIds.length
  );
}

export function applyWorkOrderScope(
  entries: WorkOrderListEntry[],
  scope: WorkOrderScope,
  currentUserId?: string,
): WorkOrderListEntry[] {
  if (scope === "all") {
    return entries;
  }
  if (scope === "active") {
    return entries.filter((entry) => ACTIVE_SCOPE_STATUSES.includes(entry.displayStatus));
  }
  if (scope === "unassigned") {
    return entries.filter((entry) => entry.assigneeIds.length === 0);
  }
  if (!currentUserId) {
    return [];
  }
  return entries.filter((entry) => workOrderMatchesUser(entry.order, currentUserId));
}

/**
 * Server filters for the workspace and phone boards.
 * Unassigned does not add a user id, so a selected owner is not OR'd into that request.
 */
export function boardWorkOrderScopeQuery(
  scope: WorkOrderScope,
  assigneeFilterIds: readonly string[],
  currentUserId?: string,
): { userId?: string; unassigned: boolean; requireUser: boolean } {
  if (scope === "unassigned") {
    return { userId: undefined, unassigned: true, requireUser: false };
  }
  const ownerIds = assigneeFilterIds.filter((id) => id !== UNASSIGNED_FILTER_VALUE);
  const mine = scope === "my";
  return {
    userId: ownerIds.length === 1 ? ownerIds[0] : mine ? currentUserId : undefined,
    unassigned: assigneeFilterIds.includes(UNASSIGNED_FILTER_VALUE),
    requireUser: mine && ownerIds.length !== 1,
  };
}

function workOrderMatchesFilterLabel(order: FactoriesWorkOrderSummary, label: WorkOrderFilterLabel): boolean {
  const card = selectWorkOrderCardPullRequest(order.pullRequests, order.id ?? "");
  if (!card) {
    return false;
  }
  if (label === "review") {
    return pullRequestState(card.pullRequest.state) === "open";
  }
  return workOrderCardPullRequestShowsMergeable(card.pullRequest, order.checkScores);
}

export function applyWorkOrderFilters(
  entries: WorkOrderListEntry[],
  filters: WorkOrderFilters,
  options: { showPullRequestMerge?: boolean } = {},
): WorkOrderListEntry[] {
  const { statuses, labels, lineIds, sourceIds, assigneeIds } = visibleWorkOrderFilters(
    filters,
    options.showPullRequestMerge ?? false,
  );
  let result = entries;
  if (statuses.length > 0) {
    result = result.filter(
      (entry) =>
        statuses.includes(entry.displayStatus) || (statuses.includes("rejected") && entry.displayStatus === "closed"),
    );
  }
  if (labels.length > 0) {
    result = result.filter((entry) => labels.some((label) => workOrderMatchesFilterLabel(entry.order, label)));
  }
  if (lineIds.length > 0) {
    result = result.filter((entry) => entry.lineIds.some((lineId) => lineIds.includes(lineId)));
  }
  if (sourceIds.length > 0) {
    result = result.filter((entry) => sourceIds.includes(entry.sourceId));
  }
  if (assigneeIds.length > 0) {
    const people = assigneeIds.filter((id) => id !== UNASSIGNED_FILTER_VALUE);
    const wantsUnassigned = assigneeIds.includes(UNASSIGNED_FILTER_VALUE);
    result = result.filter((entry) => {
      if (wantsUnassigned && entry.assigneeIds.length === 0) {
        return true;
      }
      return people.some((id) => workOrderMatchesUser(entry.order, id));
    });
  }
  return result;
}

export function applyWorkOrderSearch(entries: WorkOrderListEntry[], query: string): WorkOrderListEntry[] {
  const needle = query.trim().toLowerCase();
  if (!needle) {
    return entries;
  }
  return entries.filter((entry) => entry.searchHaystack.includes(needle));
}

export function applyWorkOrderOrdering(
  entries: WorkOrderListEntry[],
  ordering: WorkOrderOrdering,
): WorkOrderListEntry[] {
  const sorted = [...entries];
  switch (ordering) {
    case "updated":
      sorted.sort((a, b) => b.updatedAtMs - a.updatedAtMs || a.title.localeCompare(b.title));
      break;
    case "status":
      sorted.sort(
        (a, b) =>
          WORK_ORDER_DISPLAY_STATUSES.indexOf(a.displayStatus) - WORK_ORDER_DISPLAY_STATUSES.indexOf(b.displayStatus) ||
          b.updatedAtMs - a.updatedAtMs,
      );
      break;
    case "spend":
      sorted.sort((a, b) => b.totalCostCents - a.totalCostCents || b.totalTokens - a.totalTokens);
      break;
    case "key":
      sorted.sort((a, b) => b.displayNumber - a.displayNumber || a.displayKey.localeCompare(b.displayKey));
      break;
  }
  return sorted;
}

/**
 * Buckets entries into the shared board lanes, keeping the incoming order
 * inside each lane. Board and List both group this way so a task
 * never lands in different sections depending on the layout.
 */
export function groupWorkOrderEntriesByLane(
  entries: WorkOrderListEntry[],
): Map<WorkOrderBoardLaneId, WorkOrderListEntry[]> {
  const grouped = new Map<WorkOrderBoardLaneId, WorkOrderListEntry[]>(
    WORK_ORDER_BOARD_LANES.map((lane) => [lane.id, []]),
  );
  for (const entry of entries) {
    const lane = WORK_ORDER_BOARD_LANES.find((candidate) => candidate.statuses.includes(entry.displayStatus));
    if (lane) {
      grouped.get(lane.id)?.push(entry);
    }
  }
  return grouped;
}

/** Numeric suffix of `SP-42`, so `SP-10` sorts above `SP-9`. */
function parseIdentifierNumber(displayKey: string): number {
  const match = /-(\d+)$/.exec(displayKey);
  return match ? Number(match[1]) : 0;
}
